import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NewsArticle {
  id: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  author: string | null;
  category_id: string | null;
  image_url: string | null;
  featured: boolean | null;
  read_time: number | null;
  published_at: string | null;
  created_at: string;
  source: string | null;
  generated_by_ai: boolean | null;
  edited: boolean | null;
  published: boolean | null;
  views: number;
}

/** Public hook – only published articles */
export function useNewsArticles() {
  return useQuery({
    queryKey: ['news-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as NewsArticle[];
    },
  });
}

/** Public hook – latest articles for homepage */
export function useFeaturedNews(limit = 4) {
  return useQuery({
    queryKey: ['news-articles-featured', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as NewsArticle[];
    },
  });
}

/** Admin hook – all articles including drafts */
export function useAdminNewsArticles() {
  return useQuery({
    queryKey: ['news-articles-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as NewsArticle[];
    },
  });
}

export function useUpsertNewsArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (article: Partial<NewsArticle> & { title: string }) => {
      if (article.id) {
        const { error } = await supabase
          .from('news_articles')
          .update(article)
          .eq('id', article.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('news_articles')
          .insert(article);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news-articles'] });
      qc.invalidateQueries({ queryKey: ['news-articles-admin'] });
      qc.invalidateQueries({ queryKey: ['news-articles-featured'] });
    },
  });
}

export function useDeleteNewsArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('news_articles')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news-articles'] });
      qc.invalidateQueries({ queryKey: ['news-articles-admin'] });
      qc.invalidateQueries({ queryKey: ['news-articles-featured'] });
    },
  });
}

export function useIncrementNewsViews() {
  return useMutation({
    mutationFn: async (articleId: string) => {
      const { error } = await supabase.rpc('increment_news_views', { article_id: articleId });
      if (error) throw error;
    },
  });
}
